// src/pages/AdminStaffPage.js
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function StaffPage() {
  const { authFetch, user, refreshUser } = useAuth();
  const role = String(user?.role || "").toLowerCase();

  const isSuperadmin = role === "superadmin";        // ✅ keep this (your code still uses it)
  const canManage = role === "superadmin" || role === "manager";
  const canSeeActions = role === "superadmin" || role === "manager";

  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  const [loading, setLoading] = useState(true);
  const [pageErr, setPageErr] = useState("");
  const [createErr, setCreateErr] = useState("");
  const [editErr, setEditErr] = useState("");
  const [resetPwErr, setResetPwErr] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // ✅ Edit modal
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [savingEditId, setSavingEditId] = useState(null);

  // ✅ Confirm delete modal
  const [confirmDelete, setConfirmDelete] = useState({ open: false, user: null });
  const [deletingId, setDeletingId] = useState(null);

  // ✅ NEW: Reset Password modal
  const [resetPwModal, setResetPwModal] = useState({ open: false, user: null });
  const [resetPw, setResetPw] = useState("");
  const [resetPw2, setResetPw2] = useState("");
  const [resettingId, setResettingId] = useState(null);

  // ✅ NEW: extra admin actions (archive / delete+cleanup)
  const [archivingId, setArchivingId] = useState(null);
  const [cleanupDeletingId, setCleanupDeletingId] = useState(null);

  // ✅ NEW: staff flags (has schedules / in hierarchy) used for UI warnings
  // shape: { [userId]: { hasSchedules: boolean, inHierarchy: boolean } }
  const [staffFlags, setStaffFlags] = useState({});

  // ✅ Create form
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    password: "",
    role: "employee",
    department_id: "",
    job_position: "",   // ✅ NEW
    employee_id: "",
    email: "",
  });

  // ✅ Edit form (separate so we don't break create form)
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    password: "", // optional on edit (if empty -> keep old)
    role: "employee",
    department_id: "",
    job_position: "",   // ✅ NEW
    employee_id: "",
    email: "",
  });

  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of departments || []) m[String(d.id)] = d.name;
    return m;
  }, [departments]);

  const filteredStaff = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();

    return staff.filter((u) => {
      const deptName = String(
        u.department_name || deptNameById[String(u.department_id)] || ""
      ).toLowerCase();

      const roleName = String(u.role || "").toLowerCase();
      const userName = String(u.name || "").toLowerCase();
      const username = String(u.username || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const employeeId = String(u.employee_id || "").toLowerCase();
      const jobPosition = String(u.job_position || "").toLowerCase();

      const matchesSearch =
        !q ||
        userName.includes(q) ||
        username.includes(q) ||
        email.includes(q) ||
        employeeId.includes(q) ||
        jobPosition.includes(q) ||
        deptName.includes(q) ||
        roleName.includes(q);

      const matchesDepartment =
        departmentFilter === "all" ||
        String(u.department_id) === String(departmentFilter);

      const matchesRole =
        roleFilter === "all" ||
        roleName === String(roleFilter).toLowerCase();

      return matchesSearch && matchesDepartment && matchesRole;
    });
  }, [staff, searchText, departmentFilter, roleFilter, deptNameById]);

  const roleOptions = useMemo(() => {
    const roles = Array.from(
      new Set((staff || []).map((u) => String(u.role || "").trim()).filter(Boolean))
    );
    return roles.sort((a, b) => a.localeCompare(b));
  }, [staff]);

  // ✅ NEW: get real department object by id
  const deptById = useMemo(() => {
    const m = {};
    for (const d of departments || []) m[String(d.id)] = d;
    return m;
  }, [departments]);

  // ✅ NEW: department color from DB (fallback to gray)
  const deptDbColor = (deptId) => {
    const d = deptById[String(deptId)];
    return d?.color || "#64748b";
  };

  const initials = (name) => {
    const n = String(name || "").trim();
    if (!n) return "?";
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => (p?.[0] || "").toUpperCase()).join("");
  };

  // ✅ stable color per department (auto)
  const deptColor = (deptName) => {
    const s = String(deptName || "");
    if (!s) return "#64748b";
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue} 70% 40%)`;
  };

  const loadAll = async () => {
    setPageErr("");
    setSuccess("");
    setLoading(true);

    try {
      const [staffRes, deptRes] = await Promise.all([
        authFetch("/api/staff"),
        authFetch("/api/departments"),
      ]);

      const staffData = await staffRes.json();
      const deptData = await deptRes.json();

      if (!staffRes.ok) throw new Error(staffData?.error || "Failed to load staff");
      if (!deptRes.ok) throw new Error(deptData?.error || "Failed to load departments");

      const deptList = Array.isArray(deptData) ? deptData : [];
      const staffList = Array.isArray(staffData) ? staffData : [];

      setDepartments(deptList);

      // ✅ If NOT superadmin, only show staff whose department_id is in deptList
      if (!isSuperadmin) {
        const allowed = new Set(deptList.map((d) => String(d.id)));
        setStaff(staffList.filter((s) => allowed.has(String(s.department_id))));
      } else {
        setStaff(staffList);
      }
    } catch (e) {
      setPageErr(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onEditChange = (k, v) => setEditForm((p) => ({ ...p, [k]: v }));

  // ✅ ADDED: safe response reader (prevents "Unexpected token '<'" crash)
  async function safeRead(res) {
    const ct = res.headers.get("content-type") || "";
    const text = await res.text();

    if (ct.includes("application/json")) {
      try {
        return { json: JSON.parse(text), text };
      } catch {
        return { json: null, text };
      }
    }

    return { json: null, text };
  }

  function closeCreateModal() {
    setShowCreateModal(false);
    setCreateErr("");
  }

  function closeEditModal() {
    setEditModal({ open: false, user: null });
    setSavingEditId(null);
    setEditErr("");
  }

  // ✅ Extract digits from username like 6341926@fedex.com -> 6341926
  function usernameDigits(username) {
    const m = String(username || "").trim().match(/^(\d+)@fedex\.com$/i);
    return m ? m[1] : "";
  }

  // ✅ Create validation rules
  function validateCreateForm() {
    const first = String(form.first_name || "").trim();
    const last = String(form.last_name || "").trim();
    const username = String(form.username || "").trim();
    const email = String(form.email || "").trim();
    const employeeId = String(form.employee_id || "").trim();
    const deptId = String(form.department_id || "").trim();
    const password = String(form.password || "");

    if (!first) return "First name is required";
    if (!last) return "Last name is required";

    if (!username) return "Username is required";
    if (!/^\d+@fedex\.com$/i.test(username)) {
      return "Username must be like 6341926@fedex.com (numbers + @fedex.com)";
    }

    if (!employeeId) return "Employee ID is required";

    // ✅ MUST MATCH
    const digits = usernameDigits(username);
    if (digits && employeeId !== digits) {
      return `Employee ID must match username number. (Username: ${digits}@fedex.com → Employee ID must be ${digits})`;
    }

    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";

    if (!form.role) return "Role is required";
    if (!deptId) return "Department is required";

    const jobPos = String(form.job_position || "").trim();
    if (!jobPos) return "Job Position is required";

    if (!email) return "Email is required";
    if (!/^[A-Z0-9._%+-]+@fedex\.com$/i.test(email)) {
      return "Email must be a valid @fedex.com email (e.g. ramesh.mistry@fedex.com)";
    }

    return "";
  }

  // ✅ Edit validation (password optional, but username+employeeId must match)
  function validateEditForm() {
    const first = String(editForm.first_name || "").trim();
    const last = String(editForm.last_name || "").trim();
    const username = String(editForm.username || "").trim();
    const email = String(editForm.email || "").trim();
    const employeeId = String(editForm.employee_id || "").trim();
    const deptId = String(editForm.department_id || "").trim();
    const password = String(editForm.password || "");

    if (!first) return "First name is required";
    if (!last) return "Last name is required";

    if (!username) return "Username is required";
    if (!/^\d+@fedex\.com$/i.test(username)) {
      return "Username must be like 6341926@fedex.com (numbers + @fedex.com)";
    }

    if (!employeeId) return "Employee ID is required";

    const digits = usernameDigits(username);
    if (digits && employeeId !== digits) {
      return `Employee ID must match username number. (Username: ${digits}@fedex.com → Employee ID must be ${digits})`;
    }

    // password optional, but if typed, must be 8+
    if (password && password.length < 8) return "Password must be at least 8 characters";

    if (!editForm.role) return "Role is required";
    if (!deptId) return "Department is required";
    const jobPos = String(editForm.job_position || "").trim();
    if (!jobPos) return "Job Position is required";

    if (!email) return "Email is required";
    if (!/^[A-Z0-9._%+-]+@fedex\.com$/i.test(email)) {
      return "Email must be a valid @fedex.com email (e.g. ramesh.mistry@fedex.com)";
    }

    return "";
  }

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateErr("");
    setSuccess("");

    const msg = validateCreateForm();
    if (msg) return setCreateErr(msg);

    const fullName = `${String(form.first_name).trim()} ${String(form.last_name).trim()}`;
    const username = String(form.username || "").trim();
    const email = String(form.email || "").trim();

    try {
      const res = await authFetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          username,
          password: form.password,
          role: form.role,
          department_id: form.department_id ? Number(form.department_id) : null,
          job_position: String(form.job_position || "").trim(),
          employee_id: String(form.employee_id).trim(),
          email,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to create user");

      setSuccess("✅ User created successfully!");
      setCreateErr("");

      // clear form after success
      setForm((p) => ({
        ...p,
        first_name: "",
        last_name: "",
        username: "",
        password: "",
        job_position: "", // ✅ NEW
        employee_id: "",
        email: "",
      }));

      await loadAll();

      // ✅ FIX: close modal after successful create
      setShowCreateModal(false);
    } catch (e2) {
      setCreateErr(e2.message || "Create user failed");
    }
  };

  // ✅ Delete staff logic (with confirmation modal)
  // ✅ Delete staff logic (with confirmation modal)
  async function doDeleteUser(userId) {
    const id = String(userId);
    if (!id) return;

    try {
      setPageErr("");
      setSuccess("");
      setDeletingId(id);

      // ✅ NEW: use delete+cleanup endpoint (will remove schedules + clear hierarchy + delete user)
      const res = await authFetch(`/api/admin/staff/${id}/delete-cleanup`, {
        method: "POST",
        body: JSON.stringify({}),
      });

      const { json, text } = await safeRead(res);
      if (!res.ok) throw new Error(json?.error || text?.slice(0, 160) || "Failed to delete user");

      setSuccess("🧹🗑️ User deleted successfully");
      await loadAll();
    } catch (e) {
      setPageErr(e.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }

  // ✅ NEW: Load staff "flags" for UI warnings (optional endpoint)
  // If backend doesn't have this endpoint yet, this will fail silently and NOT break anything.
  async function loadStaffFlagsOptional(list) {
    try {
      const res = await authFetch(`/api/admin/staff-flags`, { method: "GET" });
      const { json, text } = await safeRead(res);
      if (!res.ok) return; // silently ignore
      if (!json || typeof json !== "object") return;

      // expected format:
      // { "1": { hasSchedules: true, inHierarchy: true }, "2": { hasSchedules: false, inHierarchy: false } }
      setStaffFlags(json);
    } catch {
      // silently ignore (endpoint may not exist yet)
    }
  }

  // ✅ after staff loads, try to fetch flags (optional)
  useEffect(() => {
    if (!Array.isArray(staff) || staff.length === 0) return;
    loadStaffFlagsOptional(staff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff.length]);

  // ✅ NEW: Archive (soft-delete) - optional endpoint
  async function doArchiveUser(userId) {
    const id = String(userId);
    if (!id) return;

    try {
      setPageErr("");
      setSuccess("");
      setArchivingId(id);

      // expected backend: PATCH /api/staff/:id/archive
      const res = await authFetch(`/api/staff/${id}/archive`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archived: 1 }),
      });

      const { json, text } = await safeRead(res);
      if (!res.ok) throw new Error(json?.error || text?.slice(0, 160) || "Failed to archive user");

      setSuccess("📦 User archived successfully (soft-delete)");
      await loadAll();
    } catch (e) {
      setPageErr(e.message || "Archive failed");
    } finally {
      setArchivingId(null);
    }
  }

  async function doAdminResetPassword(userId) {
    const id = String(userId);
    if (!id) return;

    setResetPwErr("");
    setSuccess("");

    const p1 = String(resetPw || "");
    const p2 = String(resetPw2 || "");

    if (!p1 || !p2) {
      setResetPwErr("Please enter new password and confirm it.");
      return;
    }
    if (p1 !== p2) {
      setResetPwErr("Passwords do not match.");
      return;
    }
    if (p1.length < 8) {
      setResetPwErr("New password must be at least 8 characters.");
      return;
    }

    try {
      setResettingId(id);

      const res = await authFetch(`/api/admin/users/${id}/reset-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword: p1 }),
      });

      const { json, text } = await safeRead(res);
      if (!res.ok) throw new Error(json?.error || text?.slice(0, 160) || "Reset password failed");

      setSuccess("✅ Password reset successfully.");
      setResetPw("");
      setResetPw2("");
      setResetPwModal({ open: false, user: null });
    } catch (e) {
      setResetPwErr(e.message || "Reset password failed");
    } finally {
      setResettingId(null);
    }
  }

  // ✅ NEW: Delete + Cleanup (admin button) - optional endpoint
  async function doDeleteWithCleanup(userId) {
    const id = String(userId);
    if (!id) return;

    try {
      setPageErr("");
      setSuccess("");
      setCleanupDeletingId(id);

      // expected backend:
      // POST /api/admin/staff/:id/delete-cleanup
      // (server would remove schedule entries + remove hierarchy links + then delete user)
      const res = await authFetch(`/api/admin/staff/${id}/delete-cleanup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      const { json, text } = await safeRead(res);
      if (!res.ok) throw new Error(json?.error || text?.slice(0, 160) || "Delete + cleanup failed");

      setSuccess("🧹🗑️ User deleted + cleaned up successfully");
      await loadAll();
    } catch (e) {
      setPageErr(e.message || "Delete + cleanup failed");
    } finally {
      setCleanupDeletingId(null);
    }
  }

  // ✅ Open edit modal + prefill
  function openEdit(u) {
    const name = String(u?.name || "").trim();
    const parts = name.split(/\s+/);
    const first = parts[0] || "";
    const last = parts.slice(1).join(" ") || "";

    setEditForm({
      first_name: first,
      last_name: last,
      username: u?.username || "",
      password: "", // keep empty
      role: u?.role || "employee",
      department_id: u?.department_id ? String(u.department_id) : "",
      job_position: u?.job_position ? String(u.job_position) : "", // ✅ NEW
      employee_id: u?.employee_id ? String(u.employee_id) : "",
      email: u?.email || "",
    });

    setEditModal({ open: true, user: u });
    setEditErr("");
    setSuccess("");
  }

  // ✅ Save edit
  // ✅ Save edit
  async function handleSaveEdit(e) {
    e.preventDefault();
    setEditErr("");
    setSuccess("");

    const msg = validateEditForm();
    if (msg) return setEditErr(msg);

    const u = editModal.user;
    if (!u?.id) return setEditErr("Missing user id");

    const fullName = `${String(editForm.first_name).trim()} ${String(editForm.last_name).trim()}`;

    try {
      setSavingEditId(String(u.id));

      const res = await authFetch(`/api/staff/${u.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          username: String(editForm.username).trim(),
          ...(editForm.password ? { password: editForm.password } : {}),
          role: editForm.role,
          department_id: editForm.department_id ? Number(editForm.department_id) : null,
          job_position: String(editForm.job_position || "").trim(),
          employee_id: String(editForm.employee_id).trim(),
          email: String(editForm.email).trim(),
        }),
      });

      const { json, text } = await safeRead(res);
      if (!res.ok) throw new Error(json?.error || text?.slice(0, 140) || "Failed to update user");

      // ✅ ✅ ✅ ADD THIS EXACT BLOCK RIGHT HERE
      // If you edited YOUR OWN account, refresh Auth user so job_position updates everywhere
      if (Number(u.id) === Number(user?.id)) {
        await refreshUser?.();
      }

      setSuccess("✅ User updated successfully!");
      await loadAll();
      closeEditModal();
    } catch (e2) {
      setEditErr(e2.message || "Update failed");
    } finally {
      setSavingEditId(null);
    }
  }

  // ✅ helpers for UI badges
  const getFlag = (userId) => {
    const f = staffFlags?.[String(userId)];
    return {
      hasSchedules: Boolean(f?.hasSchedules),
      inHierarchy: Boolean(f?.inHierarchy),
    };
  };

  function clearFilters() {
    setSearchText("");
    setDepartmentFilter("all");
    setRoleFilter("all");
  }

  const hasActiveFilters =
    searchText.trim() !== "" ||
    departmentFilter !== "all" ||
    roleFilter !== "all";

  return (
    <div className="staff-page">
      <style>{`
        .staff-page{
          padding: 22px 24px 32px;
          background:#f4f6fb;
          min-height: calc(100vh - 60px);
          color:#0f172a;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .header{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          margin-bottom:16px;
        }
        .header h2{ margin:0; font-size:28px; letter-spacing:-0.02em; }
        .actions{ display:flex; gap:10px; }

        .primary-btn{
          border:none;
          border-radius: 999px;
          padding:10px 14px;
          font-weight:800;
          cursor:pointer;
          color:#0b1120;
          background: linear-gradient(135deg, #38bdf8, #6366f1);
          box-shadow: 0 14px 28px rgba(15,23,42,0.14);
          transition: transform .16s ease, filter .16s ease;
        }
        .primary-btn:hover{ filter: brightness(1.03); transform: translateY(-1px); }
        .primary-btn:active{ transform: translateY(0px); }

        .notice{
          margin: 10px 0 14px;
          padding: 10px 12px;
          border-radius: 12px;
          background: #fff;
          border: 1px solid #eef2f7;
          box-shadow: 0 10px 22px rgba(15,23,42,0.06);
        }
        .notice.error{ border-color: rgba(220,38,38,0.25); }
        .notice.success{ border-color: rgba(16,185,129,0.25); }

        .modal-inline-error{
          margin: 14px 16px 0;
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #b91c1c;
          font-size: 13px;
          font-weight: 800;
        }

        /* ✅ Full width list card (no empty right space) */
        .list-card{
          width: 100%;
          background:#fff;
          border-radius: 22px;
          padding: 14px 14px 10px;
          box-shadow: 0 18px 45px rgba(15,23,42,0.08);
        }
        .list-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          padding: 8px 8px 12px;
          border-bottom: 1px solid #eef2f7;
        }
        .list-head h3{ margin:0; font-size:16px; font-weight:900; }
        .hint{ color:#64748b; font-size:12px; }

        .filter-bar{
          display:flex;
          flex-wrap:wrap;
          gap:12px;
          align-items:end;
          padding: 14px 8px 8px;
          margin-top: 2px;
        }
        .filter-item{
          display:flex;
          flex-direction:column;
          gap:6px;
          min-width: 180px;
        }
        .filter-item label{
          font-size:11px;
          color:#64748b;
          font-weight:900;
          text-transform: uppercase;
          letter-spacing: .04em;
        }
        .filter-item input,
        .filter-item select{
          height:44px;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding: 0 14px;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          outline:none;
          font-size:13px;
          color:#0f172a;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: border-color .14s ease, box-shadow .14s ease, background .14s ease, transform .14s ease;
        }
        .filter-item input:hover,
        .filter-item select:hover{
          background:#fff;
          border-color:#cbd5e1;
        }
        .filter-item input:focus,
        .filter-item select:focus{
          border-color:#93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
          background:#fff;
        }

        .clear-btn{
          height:44px;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding: 0 16px;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          color:#0f172a;
          font-size:13px;
          font-weight:900;
          cursor:pointer;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: transform .14s ease, filter .14s ease, background .14s ease, border-color .14s ease;
        }
        .clear-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.01);
          background:#fff;
          border-color:#cbd5e1;
        }
        .clear-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.01);
          background:#fff;
        }

        .table-wrap{ overflow-x:auto; }
        table{
          width:100%;
          border-collapse: separate;
          border-spacing: 0;
          margin-top: 12px;
          font-size: 13px;
          min-width: 980px;
        }
        thead th{
          text-align:left;
          padding: 12px 10px;
          color:#334155;
          background:#f8fafc;
          border-top:1px solid #eef2f7;
          border-bottom:1px solid #eef2f7;
          font-weight:900;
        }
        thead th:first-child{ border-top-left-radius: 14px; }
        thead th:last-child{ border-top-right-radius: 14px; }

        tbody td{
          padding: 12px 10px;
          border-bottom:1px solid #eef2f7;
          color:#0f172a;
          vertical-align: middle;
          background:#fff;
        }
        tbody tr:hover td{ background:#fbfdff; }

        /* ✅ Admin row highlight */
        .row-admin td{
          background: #f6f2ff !important;
        }

        .emp-cell{
          display:flex;
          align-items:center;
          gap:10px;
          min-width: 220px;
        }
        .avatar{
          width: 30px;
          height: 30px;
          border-radius: 999px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-size: 12px;
          font-weight: 900;
          color: #0b1120;
          background: #e0f2fe;
          border: 1px solid #bae6fd;
          flex: 0 0 auto;
        }
        .emp-name{ font-weight: 900; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .emp-sub{ font-size: 12px; color:#64748b; margin-top: 2px; }

        .dept-pill{
          font-weight: 900;
        }

        /* ✅ Admin special colors */
        .admin-role{ color:#7c3aed; font-weight:900; }
        .admin-username{ color:#2563eb; font-weight:900; }
        .admin-empid{ color:#dc2626; font-weight:900; }
        .admin-email{ color:#0ea5e9; font-weight:900; }

        .action-col{
          width: 140px;
          white-space: nowrap;
        }
        .icon-btn{
          width: 34px;
          height: 34px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          background: rgba(255,255,255,0.9);
          display:inline-flex;
          align-items:center;
          justify-content:center;
          cursor:pointer;
          transition: transform .14s ease, filter .14s ease, background .14s ease;
          margin-right: 8px;
        }
        .icon-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.02);
          background: #fff;
        }

        /* ✅ NEW: warning badges */
        .badge{
          display:inline-flex;
          align-items:center;
          gap:6px;
          padding: 4px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 900;
          border: 1px solid rgba(148,163,184,0.35);
          background: rgba(248,250,252,0.9);
          color:#334155;
        }
        .badge.warn{
          border-color: rgba(245,158,11,0.35);
          background: rgba(255,247,237,0.9);
          color:#9a3412;
        }
        .badge.danger{
          border-color: rgba(220,38,38,0.30);
          background: rgba(254,242,242,0.95);
          color:#991b1b;
        }

        /* Modal */
        .modal-overlay{
          position:fixed; inset:0;
          background: rgba(2,6,23,0.45);
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 24px;
          z-index: 50;
        }
        .modal{
          width: min(760px, 94vw);
          background:#fff;
          border-radius: 18px;
          box-shadow: 0 28px 70px rgba(15,23,42,0.35);
          overflow:hidden;
        }
        .modal-header{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
        }
        .modal-header h4{
          margin:0;
          font-size:16px;
          font-weight:900;
        }
        .x-btn{
          border:none;
          background:transparent;
          font-size:20px;
          cursor:pointer;
          color:#64748b;
        }
        .modal-body{
          padding: 16px;
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .field label{
          display:block;
          font-size:12px;
          color:#334155;
          font-weight:800;
          margin-bottom:6px;
        }
        .field input, .field select{
          width:100%;
          border:1px solid #e5e7eb;
          border-radius: 12px;
          padding: 10px 12px;
          background:#fff;
          outline:none;
        }
        .field input:focus, .field select:focus{
          border-color:#93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
        }
        .span2{ grid-column: 1 / -1; }

        .modal-footer{
          padding: 14px 16px;
          border-top: 1px solid #eef2f7;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }
        .btn-ghost{
          border:1px solid rgba(37,99,235,0.18);
          background: linear-gradient(135deg, #eef2ff, #e0f2fe);
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          color:#0f172a;
          transition: filter .14s ease, transform .14s ease;
        }
        .btn-ghost:hover{
          filter: brightness(1.02);
          transform: translateY(-1px);
        }
        .btn-save{
          border:none;
          border-radius: 999px;
          padding: 10px 16px;
          font-weight:900;
          cursor:pointer;
          background:#0b1120;
          color:#fff;
        }

        /* Confirm modal */
        .mini-modal{
          width: min(520px, 92vw);
          background:#fff;
          border-radius: 18px;
          box-shadow: 0 28px 70px rgba(15,23,42,0.35);
          overflow:hidden;
        }
        .mini-head{
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
          display:flex;
          align-items:center;
          justify-content:space-between;
        }
        .mini-head h4{
          margin:0;
          font-size:16px;
          font-weight:900;
        }
        .mini-body{
          padding: 14px 16px;
          color:#334155;
          font-size:14px;
          line-height:1.4;
        }
        .mini-actions{
          padding: 14px 16px;
          border-top: 1px solid #eef2f7;
          display:flex;
          justify-content:flex-end;
          gap:10px;
          flex-wrap: wrap;
        }
        .btn-cancel{
          border:1px solid #e5e7eb;
          background:#f8fafc;
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          color:#0f172a;
        }
        .btn-danger{
          border:none;
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          background:#dc2626;
          color:#fff;
        }
        .btn-danger:disabled{ opacity:.7; cursor:not-allowed; }

        /* ✅ NEW buttons */
        .btn-archive{
          border: 1px solid rgba(245,158,11,0.30);
          background: rgba(255,247,237,0.95);
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          color:#9a3412;
        }
        .btn-archive:disabled{ opacity:.7; cursor:not-allowed; }

        .btn-cleanup{
          border:none;
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          background:#0b1120;
          color:#fff;
        }
        .btn-cleanup:disabled{ opacity:.7; cursor:not-allowed; }

        @media (max-width: 760px){
          .modal-body{ grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="header">
        <div>
          <h2>Staff</h2>
        </div>

        <div className="actions">
          {canManage && (
            <button
              className="primary-btn"
              type="button"
              onClick={() => {
                setCreateErr("");
                setSuccess("");
                setShowCreateModal(true);
              }}
            >
              + Create User
            </button>
          )}
        </div>
      </div>

      {pageErr && !showCreateModal && !editModal.open && !resetPwModal?.open && (
        <div className="notice error">❌ {pageErr}</div>
      )}
      {success && <div className="notice success">{success}</div>}

      {/* ✅ Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onMouseDown={closeCreateModal}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Create User (username + password)</h4>
              <button className="x-btn" onClick={closeCreateModal} aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              {createErr && (
                <div className="modal-inline-error">
                  ❌ {createErr}
                </div>
              )}

              <div className="modal-body">
                <div className="field">
                  <label>First Name *</label>
                  <input
                    value={form.first_name}
                    onChange={(e) => onChange("first_name", e.target.value)}
                    placeholder="e.g. Ramesh"
                  />
                </div>

                <div className="field">
                  <label>Last Name *</label>
                  <input
                    value={form.last_name}
                    onChange={(e) => onChange("last_name", e.target.value)}
                    placeholder="e.g. Mistry"
                  />
                </div>

                <div className="field">
                  <label>Username *</label>
                  <input
                    value={form.username}
                    onChange={(e) => onChange("username", e.target.value)}
                    placeholder="e.g. 6341926@fedex.com"
                  />
                </div>

                <div className="field">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => onChange("password", e.target.value)}
                    placeholder="min 8 chars"
                  />
                </div>

                <div className="field">
                  <label>Role</label>
                  <select value={form.role} onChange={(e) => onChange("role", e.target.value)}>
                    <option value="employee">employee</option>
                    <option value="lead">lead</option>
                    <option value="ops_supervisor">ops_supervisor</option>
                    <option value="manager">manager</option>
                    <option value="superadmin">superadmin</option>
                  </select>
                </div>

                <div className="field">
                  <label>Department *</label>
                  <select value={form.department_id} onChange={(e) => onChange("department_id", e.target.value)}>
                    <option value="">-- Select department --</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Job Position *</label>
                  <input
                    value={form.job_position}
                    onChange={(e) => onChange("job_position", e.target.value)}
                    placeholder="e.g. Gatekeeper / Control Room / Loader"
                  />
                </div>

                <div className="field">
                  <label>Employee ID *</label>
                  <input
                    value={form.employee_id}
                    onChange={(e) => onChange("employee_id", e.target.value)}
                    placeholder="e.g. 6341926"
                  />
                </div>

                <div className="field span2">
                  <label>Email *</label>
                  <input
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    placeholder="e.g. ramesh.mistry@fedex.com"
                  />
                </div>

                <div className="span2" style={{ color: "#64748b", fontSize: 12 }}>
                  ✅ Username must be <b>numbers + @fedex.com</b> (example: <b>6341926@fedex.com</b>) <br />
                  ✅ Employee ID must match username number (example: <b>6341926</b>) <br />
                  ✅ Email must be <b>@fedex.com</b> (example: <b>ramesh.mistry@fedex.com</b>)
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-ghost" onClick={closeCreateModal}>
                  Close
                </button>
                <button type="submit" className="btn-save">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Edit User Modal */}
      {editModal.open && (
        <div className="modal-overlay" onMouseDown={closeEditModal}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Edit User</h4>
              <button className="x-btn" onClick={closeEditModal} aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleSaveEdit}>
              {editErr && (
                <div className="modal-inline-error">
                  ❌ {editErr}
                </div>
              )}

              <div className="modal-body">
                <div className="field">
                  <label>First Name *</label>
                  <input value={editForm.first_name} onChange={(e) => onEditChange("first_name", e.target.value)} />
                </div>

                <div className="field">
                  <label>Last Name *</label>
                  <input value={editForm.last_name} onChange={(e) => onEditChange("last_name", e.target.value)} />
                </div>

                <div className="field">
                  <label>Username *</label>
                  <input
                    value={editForm.username}
                    onChange={(e) => onEditChange("username", e.target.value)}
                    placeholder="e.g. 6341926@fedex.com"
                  />
                </div>

                <div className="field">
                  <label>Password (optional)</label>
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(e) => onEditChange("password", e.target.value)}
                    placeholder="leave blank to keep same"
                  />
                </div>

                <div className="field">
                  <label>Role</label>
                  <select value={editForm.role} onChange={(e) => onEditChange("role", e.target.value)}>
                    <option value="employee">employee</option>
                    <option value="lead">lead</option>
                    <option value="ops_supervisor">ops_supervisor</option>
                    <option value="manager">manager</option>
                    <option value="superadmin">superadmin</option>
                  </select>
                </div>

                <div className="field">
                  <label>Department *</label>
                  <select value={editForm.department_id} onChange={(e) => onEditChange("department_id", e.target.value)}>
                    <option value="">-- Select department --</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Job Position *</label>
                  <input
                    value={editForm.job_position}
                    onChange={(e) => onEditChange("job_position", e.target.value)}
                    placeholder="e.g. Gatekeeper / Control Room / Loader"
                  />
                </div>

                <div className="field">
                  <label>Employee ID *</label>
                  <input value={editForm.employee_id} onChange={(e) => onEditChange("employee_id", e.target.value)} />
                </div>

                <div className="field span2">
                  <label>Email *</label>
                  <input value={editForm.email} onChange={(e) => onEditChange("email", e.target.value)} />
                </div>

                <div className="span2" style={{ color: "#64748b", fontSize: 12 }}>
                  ✅ Username must be <b>numbers + @fedex.com</b> and Employee ID must match username number.
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-ghost" onClick={closeEditModal}>
                  Close
                </button>
                <button type="submit" className="btn-save" disabled={savingEditId === String(editModal.user?.id || "")}>
                  {savingEditId === String(editModal.user?.id || "") ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Staff List */}
      <div className="list-card">
        <div className="list-head">
          <div>
            <h3>Employee List</h3>
            <div className="hint">Shows username, role, department, employee ID, and email</div>
          </div>
          <div className="hint">{loading ? "Loading..." : `Total: ${filteredStaff.length}`}</div>
        </div>

        <div className="filter-bar">
          <div className="filter-item" style={{ flex: "1 1 280px" }}>
            <label>Search</label>
            <input
              type="text"
              placeholder="Search name, username, email, employee ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Department</label>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="all">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <div className="filter-item" style={{ minWidth: "150px" }}>
              <label>&nbsp;</label>
              <button type="button" className="clear-btn" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          )}
        </div>
        {loading ? (
          <p style={{ padding: 12 }}>Loading...</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>ID</th>
                  <th>Employee Name</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Job Position</th>
                  <th>Employee ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  {canSeeActions && <th className="action-col">Action</th>}
                </tr>
              </thead>

              <tbody>
                {filteredStaff.map((u) => {
                  const deptName = u.department_name || deptNameById[String(u.department_id)] || "—";
                  const deptColorReal = deptDbColor(u.department_id);
                  const isAdmin = String(u.role).toLowerCase() === "superadmin";
                  const flags = getFlag(u.id);

                  return (
                    <tr key={u.id} className={isAdmin ? "row-admin" : ""}>
                      <td>{u.id}</td>

                      <td>
                        <div className="emp-cell">
                          <div className="avatar">{initials(u.name)}</div>
                          <div>
                            <div className="emp-name" style={isAdmin ? { color: "#7c3aed" } : undefined}>
                              {u.name}

                              {/* ✅ NEW: UI warning badges (only shows if backend provides staffFlags endpoint) */}
                              {flags.hasSchedules && <span className="badge warn">📅 This user has schedules</span>}
                              {flags.inHierarchy && <span className="badge danger">🏷️ Used in dept hierarchy</span>}
                            </div>

                            <div className="emp-sub" style={{ color: deptColorReal, fontWeight: 900 }}>
                              {deptName}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className={isAdmin ? "admin-role" : ""}>{u.role}</td>
                      <td className="dept-pill" style={{ color: deptColorReal }}>
                        {deptName}
                      </td>

                      <td style={{ fontWeight: 800 }}>
                        {u.job_position || "—"}
                      </td>

                      <td className={isAdmin ? "admin-empid" : ""}>{u.employee_id || "—"}</td>
                      <td className={isAdmin ? "admin-username" : ""}>{u.username || "—"}</td>
                      <td className={isAdmin ? "admin-email" : ""}>{u.email || "—"}</td>

                      {canSeeActions && (
                        <td className="action-col">
                          {/* Edit */}
                          <button
                            type="button"
                            className="icon-btn"
                            title="Edit user"
                            aria-label={`Edit ${u.name}`}
                            onClick={() => openEdit(u)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M12 20h9"
                                stroke="#2563eb"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
                                stroke="#2563eb"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>

                          {/* Reset Password */}
                          <button
                            type="button"
                            className="icon-btn"
                            title="Reset password"
                            aria-label={`Reset password for ${u.name}`}
                            onClick={() => {
                              setResetPwErr("");
                              setSuccess("");
                              setResetPw("");
                              setResetPw2("");
                              setResetPwModal({ open: true, user: u });
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
                                stroke="#0f172a"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M7 10V7a5 5 0 0 1 10 0v3"
                                stroke="#0f172a"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 10h12v10H6V10z"
                                stroke="#0f172a"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            className="icon-btn"
                            title="Delete user"
                            aria-label={`Delete ${u.name}`}
                            onClick={() => setConfirmDelete({ open: true, user: u })}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M3 6h18"
                                stroke="#dc2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 6V4h8v2"
                                stroke="#dc2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 6l1 16h10l1-16"
                                stroke="#dc2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path d="M10 11v6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                              <path d="M14 11v6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}

                {filteredStaff.length === 0 && (
                  <tr>
                    <td colSpan={canSeeActions ? 9 : 8} style={{ textAlign: "center", padding: 18, color: "#64748b" }}>
                      No staff found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ✅ Confirm Delete Modal */}
      {confirmDelete.open && (
        <div className="modal-overlay" onMouseDown={() => setConfirmDelete({ open: false, user: null })}>
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>Delete User</h4>
              <button className="x-btn" onClick={() => setConfirmDelete({ open: false, user: null })} aria-label="Close">
                ×
              </button>
            </div>

            <div className="mini-body">
              Are you sure you want to delete <b>{confirmDelete.user?.name || "this user"}</b>?
              <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>
                This will remove the login account and staff entry (based on backend rules).
              </div>

              {/* ✅ NEW: UI warning (if flags available) */}
              {(() => {
                const u = confirmDelete.user;
                const flags = u?.id ? getFlag(u.id) : { hasSchedules: false, inHierarchy: false };
                if (!flags.hasSchedules && !flags.inHierarchy) return null;

                return (
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                    {flags.hasSchedules && (
                      <div className="badge warn" style={{ width: "fit-content" }}>
                        📅 This user has schedules
                      </div>
                    )}
                    {flags.inHierarchy && (
                      <div className="badge danger" style={{ width: "fit-content" }}>
                        🏷️ This user is used as Manager/Supervisor/Lead
                      </div>
                    )}
                    <div style={{ color: "#64748b", fontSize: 12 }}>
                      Tip: If delete is blocked, use <b>Archive (soft-delete)</b>. If you really want to remove, use{" "}
                      <b>Delete + Cleanup</b> (admin tool).
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="mini-actions">
              <button className="btn-cancel" type="button" onClick={() => setConfirmDelete({ open: false, user: null })}>
                Cancel
              </button>

              {/* ✅ NEW: Archive (soft-delete) */}
              <button
                className="btn-archive"
                type="button"
                disabled={archivingId === String(confirmDelete.user?.id || "")}
                onClick={async () => {
                  const id = confirmDelete.user?.id;
                  setConfirmDelete({ open: false, user: null });
                  if (!id) return;
                  await doArchiveUser(id);
                }}
                title="Soft-delete (archive) user"
              >
                {archivingId === String(confirmDelete.user?.id || "") ? "Archiving..." : "Archive User"}
              </button>

              {/* ✅ NEW: Delete + Cleanup (admin) */}
              <button
                className="btn-cleanup"
                type="button"
                disabled={cleanupDeletingId === String(confirmDelete.user?.id || "")}
                onClick={async () => {
                  const id = confirmDelete.user?.id;
                  setConfirmDelete({ open: false, user: null });
                  if (!id) return;
                  await doDeleteWithCleanup(id);
                }}
                title="Delete user + cleanup schedules & hierarchy (requires backend endpoint)"
              >
                {cleanupDeletingId === String(confirmDelete.user?.id || "") ? "Cleaning..." : "Delete + Cleanup"}
              </button>

              {/* Existing Delete */}
              <button
                className="btn-danger"
                type="button"
                disabled={deletingId === String(confirmDelete.user?.id || "")}
                onClick={async () => {
                  const id = confirmDelete.user?.id;
                  setConfirmDelete({ open: false, user: null });
                  if (!id) return;
                  await doDeleteUser(id);
                }}
              >
                {deletingId === String(confirmDelete.user?.id || "") ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Reset Password Modal (PASTE RIGHT HERE) */}
      {resetPwModal?.open && (
        <div className="modal-overlay" onMouseDown={() => setResetPwModal({ open: false, user: null })}>
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>Reset Password</h4>
              <button className="x-btn" onClick={() => setResetPwModal({ open: false, user: null })} aria-label="Close">
                ×
              </button>
            </div>

            <div className="mini-body">
              {resetPwErr && (
                <div className="modal-inline-error" style={{ margin: "0 0 12px 0" }}>
                  ❌ {resetPwErr}
                </div>
              )}

              Reset password for <b>{resetPwModal.user?.name || "user"}</b>

              <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                <div className="field span2">
                  <label>New Password *</label>
                  <input
                    type="password"
                    value={resetPw}
                    onChange={(e) => setResetPw(e.target.value)}
                    placeholder="min 8 characters"
                  />
                </div>

                <div className="field span2">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    value={resetPw2}
                    onChange={(e) => setResetPw2(e.target.value)}
                    placeholder="retype new password"
                  />
                </div>

                <div style={{ color: "#64748b", fontSize: 12 }}>
                  ✅ After reset, user can login with the new password.
                </div>
              </div>
            </div>

            <div className="mini-actions">
              <button className="btn-cancel" type="button" onClick={() => setResetPwModal({ open: false, user: null })}>
                Cancel
              </button>

              <button
                className="btn-cleanup"
                type="button"
                disabled={resettingId === String(resetPwModal.user?.id || "")}
                onClick={async () => {
                  const id = resetPwModal.user?.id;
                  if (!id) return;
                  await doAdminResetPassword(id);
                }}
              >
                {resettingId === String(resetPwModal.user?.id || "") ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
